import { useEffect, useRef, useState } from 'react';
import {
  Checkbox,
  DatePicker,
  Dialog,
  Form,
  MessagePlugin,
  Select,
  Textarea,
  Upload,
} from 'tdesign-react';
import type {
  FormInstanceFunctions,
  FormRule,
  SubmitContext,
  UploadFile,
  UploadProps,
} from 'tdesign-react';
import {
  CommandType,
  SUB_CATEGORY_OPTIONS,
  uploadCommandFiles,
  type CreateCommandPayload,
} from '@/services/command';
import './CommandFormDialog.css';

const { FormItem } = Form;

const ALLOWED_EXTS = [
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.zip',
  '.rar',
  '.csv',
  '.txt',
  '.png',
  '.jpg',
  '.jpeg',
];

/** 单文件大小上限（与后端 MAX_FILE_SIZE 保持一致） */
const MAX_FILE_SIZE = 50 * 1024 * 1024;
/** 一次最多上传的文件数（与后端 MAX_FILE_COUNT 保持一致） */
const MAX_FILE_COUNT = 10;
/** 全部文件总大小上限（与后端 MAX_TOTAL_FILE_SIZE 保持一致） */
const MAX_TOTAL_FILE_SIZE = 100 * 1024 * 1024;
/** 指令内容前端最大长度（与后端 DTO @MaxLength(5000) 一致） */
const MAX_CONTENT_LENGTH = 5000;

/**
 * 当前系统仅开放「个保/网安线索」这一种类型（前台门户提交也是同样处理），
 * 因此后台新建弹窗不再让用户选择指令类型，提交时固定使用该类型，
 * 后端 command_type 字段仍保留以便未来扩展。
 */
const DEFAULT_COMMAND_TYPE: CommandType = CommandType.NotifyOnly;

interface FormValue {
  subCategory?: number;
  content?: string;
  needReply?: boolean;
  /** 截止日期（YYYY-MM-DD，精确到日）；仅 needReply=true 时使用；允许为空 */
  deadlineDate?: string;
}

const emptyValue: FormValue = {
  subCategory: undefined,
  content: '',
  needReply: false,
  deadlineDate: '',
};

const rules: Record<keyof FormValue, FormRule[]> = {
  subCategory: [{ required: true, message: '请选择指令分类', type: 'error' }],
  // content 与文件二选一必填 —— 组合校验放到 handleSubmit 里做，Form 层面仅校验长度上限
  content: [
    {
      max: MAX_CONTENT_LENGTH,
      message: `指令内容长度不能超过 ${MAX_CONTENT_LENGTH} 字`,
      type: 'error',
    },
  ],
  // needReply 允许为空（默认 false），不做必填校验
  needReply: [],
  // deadlineDate 允许为空；只在 needReply=true 时展示，Form 层面不做必填
  deadlineDate: [],
};

interface CommandFormDialogProps {
  visible: boolean;
  loading?: boolean;
  onCancel: () => void;
  onSubmit: (payload: CreateCommandPayload) => Promise<void> | void;
}

/** 单个文件类型/大小校验，通过返回 null，否则返回错误信息 */
const validateSingleFile = (file: File): string | null => {
  const name = file.name || '';
  const ext = name.includes('.') ? name.slice(name.lastIndexOf('.')).toLowerCase() : '';
  if (!ALLOWED_EXTS.includes(ext)) {
    return `不支持的文件类型（允许：${ALLOWED_EXTS.join(', ')}）`;
  }
  if (file.size > MAX_FILE_SIZE) {
    return `文件「${name}」超过单文件 50MB 上限`;
  }
  return null;
};

const CommandFormDialog = ({ visible, loading, onCancel, onSubmit }: CommandFormDialogProps) => {
  const formRef = useRef<FormInstanceFunctions>(null);
  /**
   * 待提交的文件列表 —— 只在前端内存里暂存，用户点「确定」时才一次性 multipart 上传，
   * 由服务端在同一请求内完成"单文件直传 or 多文件打包 zip 再传 COS"。
   */
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  /**
   * 需回复勾选状态副本（与表单同源，只是为了在 render 中控制截止日期字段是否 disabled，
   * 因为直接读 formRef 拿不到响应式的最新值）。
   */
  const [needReplyChecked, setNeedReplyChecked] = useState(false);

  useEffect(() => {
    if (!visible) return;
    formRef.current?.reset();
    setFiles([]);
    setUploading(false);
    setNeedReplyChecked(false);
  }, [visible]);

  /**
   * 文件列表变化：加入新文件时做数量/单文件/总大小校验（校验不通过则拒绝更新）。
   */
  const handleFilesChange: UploadProps['onChange'] = (next) => {
    const list = (next as UploadFile[]) ?? [];

    // 数量
    if (list.length > MAX_FILE_COUNT) {
      MessagePlugin.error(`最多上传 ${MAX_FILE_COUNT} 个文件`);
      return;
    }

    // 单文件类型/大小 + 累加总大小
    let totalSize = 0;
    for (const item of list) {
      const raw = item.raw as File | undefined;
      if (!raw) continue;
      const err = validateSingleFile(raw);
      if (err) {
        MessagePlugin.error(err);
        return;
      }
      totalSize += raw.size;
    }
    if (totalSize > MAX_TOTAL_FILE_SIZE) {
      MessagePlugin.error(`所有文件总大小不能超过 ${MAX_TOTAL_FILE_SIZE / 1024 / 1024}MB`);
      return;
    }

    setFiles(
      list.map((f) => ({
        ...f,
        status: 'waiting',
        percent: 0,
      })),
    );
  };

  const handleSubmit = async (ctx: SubmitContext) => {
    if (ctx.validateResult !== true) return;
    const values = formRef.current?.getFieldsValue?.(true) as FormValue | undefined;
    if (!values?.subCategory) return;

    const trimmedContent = (values.content ?? '').trim();
    const hasContent = trimmedContent.length > 0;
    const hasFile = files.length > 0;
    // 业务规则：指令内容 / 文件 至少存在其一
    if (!hasContent && !hasFile) {
      MessagePlugin.error('请填写指令内容或上传文件（至少一项）');
      return;
    }

    const rawFiles = hasFile
      ? files.map((f) => f.raw as File | undefined).filter((f): f is File => !!f)
      : [];
    if (hasFile && rawFiles.length === 0) {
      MessagePlugin.error('未识别到有效文件，请重新选择');
      return;
    }

    setUploading(true);
    try {
      // 1) 有文件才上传：一次性 multipart 上传，服务端在同一请求内完成打包 + 传 COS；
      //    仅文本内容时跳过上传，直接落库
      let meta: { fileKey: string; fileName: string; fileSize: number } | null = null;
      if (rawFiles.length > 0) {
        setFiles((prev) => prev.map((f) => ({ ...f, status: 'progress', percent: 0 })));
        meta = await uploadCommandFiles(rawFiles, (percent) => {
          setFiles((prev) => prev.map((f) => ({ ...f, status: 'progress', percent })));
        });
        setFiles((prev) => prev.map((f) => ({ ...f, status: 'success', percent: 100 })));
      }

      // 2) 再走外层提交（落库） —— 错误由外层拦截器统一 toast
      //    commandType 前端固定为「个保/网安线索」；DB/DTO 字段保留以便后续扩展。
      await onSubmit({
        commandType: DEFAULT_COMMAND_TYPE,
        subCategory: values.subCategory,
        needReply: !!values.needReply,
        // 只在勾选需回复且填了日期时带上；未勾则直接省略（后端会兜底丢弃）
        ...(values.needReply && values.deadlineDate ? { deadlineDate: values.deadlineDate } : {}),
        ...(hasContent ? { content: trimmedContent } : {}),
        ...(meta ?? {}),
      });
    } catch (err) {
      // 上传/落库失败：文件列表状态回退到 waiting，允许用户点确定再次尝试
      setFiles((prev) => prev.map((f) => ({ ...f, status: 'waiting', percent: 0 })));
      const msg = err instanceof Error ? err.message : '提交失败';
      MessagePlugin.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleConfirm = () => {
    formRef.current?.submit?.();
  };

  return (
    <Dialog
      header="新增指令"
      visible={visible}
      onClose={onCancel}
      onCancel={onCancel}
      onConfirm={handleConfirm}
      confirmLoading={loading || uploading}
      destroyOnClose
      // 宽度加大到 720：560 太窄，Upload file-flow 主题下文件行 + 操作按钮会被截断；
      // 720 能完整容纳「文件名 + 大小 + 状态 + 删除按钮」一整行，同时在 1280 视口仍留足边距。
      width={720}
      // placement="center"：弹窗在视口垂直居中（默认 top 定位会显得偏上，页面内容多时更明显）
      placement="center"
      // 顶层类名：给弹窗内的 Upload / Form 控件做精细样式覆盖，避免长 tips / 拖拽区撑出横向滚动条
      dialogClassName="command-form-dialog"
    >
      <Form
        ref={formRef}
        initialData={emptyValue}
        rules={rules}
        labelWidth={100}
        onSubmit={handleSubmit}
        resetType="initial"
      >
        <FormItem label="指令分类" name="subCategory">
          <Select options={[...SUB_CATEGORY_OPTIONS]} placeholder="请选择指令分类" clearable />
        </FormItem>
        <FormItem label="指令内容" name="content" help="与「上传文件」至少填写一项">
          <Textarea
            placeholder="可填写指令的文字说明（线索来源 / 处置要求等），仅提交文件时可留空"
            autosize={{ minRows: 4, maxRows: 10 }}
            maxlength={MAX_CONTENT_LENGTH}
          />
        </FormItem>
        <FormItem label="是否需反馈" name="needReply">
          {/*
            勾选后：
              - 总群通知卡片会显示"需要回复"标识；
              - 子群「已完成」按钮点击时不直接置完成，而是引导操作人到 Web 完成页
                上传回执附件后再落库完成。
            用 onChange 同步 needReplyChecked，让下方"截止日期"字段能响应式 enable/disable。
            并在取消勾选时清空已选日期，避免"看似不需回复但仍带截止日"的脏数据。
          */}
          <Checkbox
            onChange={(v) => {
              const next = Boolean(v);
              setNeedReplyChecked(next);
              if (!next) formRef.current?.setFieldsValue?.({ deadlineDate: '' });
            }}
          >
            需反馈（点击「已完成」时需上传回执附件）
          </Checkbox>
        </FormItem>
        {/*
          截止日期：仅在勾了需回复时可编辑。
          - 允许留空：勾了需回复但不填也可以提交
          - disableDate 屏蔽今天之前的日期
        */}
        <FormItem
          label="反馈日期"
          name="deadlineDate"
          help="仅在勾选「需反馈」时生效；当日 11:00 未完成会主群自动提醒"
        >
          <DatePicker
            valueType="YYYY-MM-DD"
            format="YYYY-MM-DD"
            placeholder="（可留空）"
            clearable
            disabled={!needReplyChecked}
            disableDate={{ before: new Date().toISOString().slice(0, 10) }}
          />
        </FormItem>
        <FormItem label="上传文件" help="与「指令内容」至少填写一项">
          <Upload
            files={files}
            onChange={handleFilesChange}
            autoUpload={false}
            multiple
            max={MAX_FILE_COUNT}
            accept={ALLOWED_EXTS.join(',')}
            tips={`最多 ${MAX_FILE_COUNT} 个文件，单文件 ≤ 50MB，合计 ≤ 100MB；多文件将自动打包为 zip。`}
            theme="file-flow"
            draggable
            disabled={uploading}
            // 隐藏 file-flow 主题默认的「选择文件 / 取消上传」按钮：
            // 前者与拖拽区域重复；后者仅在组件内置上传流程有意义，
            // 我们走「点击弹窗确定时统一上传」，此按钮点了没效果。
            uploadButton={null}
            cancelUploadButton={null}
          />
        </FormItem>
      </Form>
    </Dialog>
  );
};

export default CommandFormDialog;
